/**
 * Service Types & Interfaces
 * 
 * Centralized type definitions for all service layer
 */

// ========================================
// Common API Types
// ========================================

/**
 * Standard API Error Structure
 * This structure is used across all API endpoints for consistent error handling
 */
export interface ApiError {
  code: string;
  message: string;
  data?: any;
  exceptionName?: string;
  stackTrace?: string;
}

/**
 * Standard Empty Response Structure
 * Used for APIs that return empty data (Create, Update, Delete operations)
 * Backend typically returns an empty object in the data field for these operations
 */
export interface EmptyResponse {
  data: any; // Backend returns empty object
  code?: string;
  message?: string;
  exceptionName?: string;
  stackTrace?: string;
}

// ========================================
// Authentication Types
// ========================================

/**
 * User model from backend
 */
export interface User {
  resetPasswordAllowed: boolean;
  id: string;
  email: string;
  userName: string;
  firstName: string;
  lastName: string;
  identityProvider: string;
  userRoles: string[];
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  phoneNumber: string | null;
  companyId: string;
  companyName: string | null;
  userType: string;
  invitedById: string | null;
  jobTitle: string | null;
  department: string | null;
  location: string | null;
  requiresOnboarding: boolean;
  passwordChangeRequired: boolean;
  companyImageUrl: string | null; // New field for company logo
}

export interface AuthInfo {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  user: User;
}

/**
 * Token Info stored in storage
 */
export interface TokenInfo {
  accessToken: string;
  refreshToken: string;
}

// ========================================
// API Request Types
// ========================================

export interface SignInRequest {
  email: string;
  password: string;
}

export interface ChangeTemporaryPasswordRequest {
  email: string;
  password: string;
  passwordConfirmation: string;
  currentPassword: string;
}

export interface ChangePasswordRequest {
  password: string;
  passwordConfirmation: string;
  currentPassword: string;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  email: string;
  verificationToken: string;
  verificationCode: string;
  password: string;
  passwordConfirmation: string;
}

export interface VerifyOTPRequest {
  email: string;
  verificationToken: string;
  verificationCode: string;
}

export interface RefreshTokenRequest {
  refreshToken: string;
}

/**
 * Update Profile Request Types
 */
export interface UpdateInternalUserProfileRequest {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  companyName: string;
}

export interface UpdateProcessOwnerProfileRequest {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  jobTitle: string;
  department: string;
}

export type UpdateProfileRequest = UpdateInternalUserProfileRequest | UpdateProcessOwnerProfileRequest;

/**
 * Update Annual Report Request
 */
export interface UpdateAnnualReportRequest {
  fiscalYearEndDate: string;
  annualReportId: string;
}

// ========================================
// API Response Types
// ========================================

/**
 * Authentication Response (used for Sign In, Change Password, Refresh Token)
 * All these endpoints return the same structure
 */
export interface AuthResponse {
  data: AuthInfo;
  code: string;
  message: string;
  exceptionName?: string;
  stackTrace?: string;
}

export interface ProfileResponse {
  data: User;
  code: string;
  message: string;
  exceptionName?: string;
  stackTrace?: string;
}

export interface ForgotPasswordResponse {
  data: {
    verificationToken: string;
  };
  code: string;
  message: string;
  exceptionName?: string;
  stackTrace?: string;
}

export interface ResetPasswordResponse {
  data: Record<string, never>;
  code: string;
  message: string;
  exceptionName?: string;
  stackTrace?: string;
}

export interface VerifyOTPResponse {
  data: Record<string, never>;
  code: string;
  message: string;
  exceptionName?: string;
  stackTrace?: string;
}

export type RiskRanking = 'High' | 'Med' | 'Low' | 'Unranked' | 'null';
export type RiskRankingApi = 'unranked' | 'high' | 'med' | 'low';
export interface SortDescriptor {
  name: string;
  direction: 'ascending' | 'descending';
}
export interface FileUpload {
  id: string;
  name: string;
}

// ========================================
// Syncfusion Grid Types
// ========================================

/**
 * Syncfusion Search Parameter
 * Used for server-side search functionality
 */
export interface SyncfusionSearchParam {
  fields: string[];
  key: string;
  operator: 'contains' | 'startswith' | 'endswith' | 'equal';
  ignoreAccent?: boolean;
}

/**
 * Syncfusion Grid Request Parameters
 * Used for server-side paging, sorting, filtering, searching
 */
export interface SyncfusionGridParams {
  skip: number;
  take: number;
  requiresCounts?: boolean;
  sorted?: SortDescriptor[];
  where?: any[];
  search?: SyncfusionSearchParam[];
}

/**
 * Map backend risk ranking to frontend
 */
export const mapRiskRanking = (ranking: string): RiskRanking => {
  const normalized = ranking.toLowerCase();
  if (normalized === 'high') return 'High';
  if (normalized === 'med') return 'Med';
  if (normalized === 'low') return 'Low';
  return 'Unranked';
};

/**
 * Map frontend risk ranking to API
 */
export const mapRiskRankingToApi = (ranking: RiskRanking): RiskRankingApi => {
  switch (ranking) {
    case 'High': return 'high';
    case 'Med': return 'med';
    case 'Low': return 'low';
    default: return 'unranked';
  }
};

// ========================================
// Search Utilities
// ========================================

/**
 * The backend uses a unified SearchIndex field that contains all searchable data.
 * This simplifies the search - we always search against this single field.
 */
export const SEARCH_INDEX_FIELD = 'SearchIndex';

/**
 * Build Syncfusion search params array using the unified SearchIndex field.
 * Backend handles mapping to relevant fields internally.
 */
export const buildSearchParams = (
  searchTerm: string
): SyncfusionSearchParam[] | undefined => {
  if (!searchTerm || searchTerm.trim() === '') {
    return undefined;
  }
  
  return [{
    fields: [SEARCH_INDEX_FIELD],
    key: searchTerm.trim(),
    operator: 'contains' as const,
    ignoreAccent: false,
  }];
};